import { useState } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { useXmrPrice } from '@/hooks/useXmrPrice';
import {
  formatChange,
  formatQuote,
  isStale,
  type XmrPair,
} from '@/lib/prices';
import { NAV_LINK_BASE, NAV_LINK_BASE_COMPACT } from '@/lib/navTheme';
import { timeAgo } from '@/lib/stashstr';
import { cn } from '@/lib/utils';

const PAIR_KEY = 'stashstr-xmr-pair';
const VALID: XmrPair[] = ['USD', 'EUR', 'BTC'];

function readStoredPair(): XmrPair {
  try {
    const stored = window.localStorage.getItem(PAIR_KEY) as XmrPair | null;
    return stored && VALID.includes(stored) ? stored : 'USD';
  } catch {
    return 'USD';
  }
}

function writeStoredPair(pair: XmrPair): void {
  try {
    window.localStorage.setItem(PAIR_KEY, pair);
  } catch {
    // storage unavailable — non-fatal
  }
}

/**
 * Live XMR price ticker. Click cycles USD -> EUR -> BTC (never navigates);
 * the selected pair persists across navigation. Shows last-known price +
 * amber stale dot when offline; tooltip carries last-checked age + source
 * and refreshes on hover. Nav chrome matches the stasher nav scheme
 * (Chakra Petch, uppercase, brightness ramp); price/change keep their
 * data colors.
 */
export function XmrTicker({ compact }: { compact?: boolean }) {
  const { data } = useXmrPrice();
  const [pair, setPair] = useState<XmrPair>(readStoredPair);
  const [, setNowTick] = useState(0);

  if (!data) {
    return <Skeleton className={compact ? 'h-4 w-20' : 'h-5 w-32'} />;
  }

  // BTC leg never shows a change (locked spec); CoinGecko fallback has none.
  const chg =
    pair === 'USD' ? data.chgUsd : pair === 'EUR' ? data.chgEur : null;
  const stale = isStale(data.updatedAt);
  const ago = timeAgo(Math.floor(data.updatedAt / 1000));

  const cycle = () => {
    const next: XmrPair = pair === 'USD' ? 'EUR' : pair === 'EUR' ? 'BTC' : 'USD';
    setPair(next);
    writeStoredPair(next);
  };

  return (
    <button
      type="button"
      onClick={cycle}
      // Hover bumps state -> re-render -> timeAgo recomputes fresh.
      onMouseEnter={() => setNowTick(Date.now())}
      onFocus={() => setNowTick(Date.now())}
      title={`XMR/${pair} · click to cycle · Last checked ${ago} · ${data.source}${stale ? ' · stale' : ''}`}
      aria-label={`Monero price ${formatQuote(pair, data)}, ${formatChange(chg)}. Activate to cycle currency.`}
      className={cn(
        compact ? NAV_LINK_BASE_COMPACT : NAV_LINK_BASE,
        'inline-flex items-center gap-1.5 whitespace-nowrap',
      )}
    >
      <span className="font-bold text-[#f97316] dark:text-[#fb923c]">XMR</span>
      <span>{formatQuote(pair, data)}</span>
      <span
        className={cn(
          chg === null
            ? 'text-gray-400 dark:text-gray-500'
            : chg >= 0
              ? 'text-[#198754]'
              : 'text-[#dc3545]',
        )}
      >
        {formatChange(chg)}
      </span>
      {stale && <span className="size-1.5 rounded-full bg-amber-500" aria-hidden="true" />}
    </button>
  );
}
